/**
 * VariableEditor Component
 *
 * Editor for filling in {{1}}, {{2}}, … placeholders of a WhatsApp template.
 * Highlights the relevant variable in the template body as the user edits it.
 */

import { Hash, Image as ImageIcon, Type as TypeIcon, Wand2 } from "lucide-react";
import { useRef, useState, useCallback, useMemo } from "react";
import type { WhatsAppTemplate } from "../types";
import {
  PersonalizationToggle,
  DYNAMIC_FIELDS,
  type DynamicFieldKey,
} from "./PersonalizationToggle";

interface VariableEditorProps {
  template: WhatsAppTemplate;
  bodyVariables: Record<string, string>;
  /** Per-variable dynamic field mapping. Null = static value */
  dynamicFields: Record<string, DynamicFieldKey | null>;
  headerVariable?: string;
  headerMediaUrl?: string;
  onBodyVariableChange: (index: number, value: string) => void;
  onDynamicFieldChange: (index: number, field: DynamicFieldKey | null) => void;
  onHeaderVariableChange: (value: string) => void;
  onHeaderMediaChange: (dataUrl: string | null) => void;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

/**
 * Recommendations per variable index to help marketers fill in the right info.
 */
const VARIABLE_HINTS: Record<number, string> = {
  1: "Ej: nombre del cliente",
  2: "Ej: % de descuento",
  3: "Ej: código del cupón",
  4: "Ej: tiempo estimado",
};

/**
 * Infer the dynamic field that's most likely the right pick for a given
 * {{N}} placeholder by inspecting the template body around it.
 *
 * Example: body = "Hola {{1}}, dejaste {{2}} productos" → {{1}} is name.
 */
function inferDynamicField(
  body: string,
  index: number,
): DynamicFieldKey | null {
  const placeholder = `{{${index}}}`;
  const idx = body.indexOf(placeholder);
  if (idx < 0) return null;
  const window = body.slice(Math.max(0, idx - 30), idx + placeholder.length + 5).toLowerCase();

  if (/hola|hi |estimad|querid/.test(window)) return "firstName";
  if (/email|correo/.test(window)) return "email";
  if (/total|gast|importe/.test(window)) return "totalGastado";
  if (/pedido|orden|compra/.test(window) && /n[º°]?|#/.test(window)) return "cantidadPedidos";
  if (/puntos|points/.test(window)) return "totalPoints";
  if (/descuento|off|%/.test(window)) return "discountPct";
  if (/categoria|vip|tier|nivel/.test(window)) return "userCategory";
  return null;
}

export function VariableEditor({
  template,
  bodyVariables,
  dynamicFields,
  headerVariable,
  headerMediaUrl,
  onBodyVariableChange,
  onDynamicFieldChange,
  onHeaderVariableChange,
  onHeaderMediaChange,
}: VariableEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  /** Inferred suggestions (used as hints — user can override) */
  const inferred = useMemo(() => {
    const map: Record<number, DynamicFieldKey | null> = {};
    for (let i = 1; i <= template.variableCount; i++) {
      map[i] = inferDynamicField(template.body, i);
    }
    return map;
  }, [template.body, template.variableCount]);

  const handleAutoFill = useCallback(() => {
    for (let i = 1; i <= template.variableCount; i++) {
      const guess = inferred[i];
      if (guess && !dynamicFields[String(i)]) {
        onDynamicFieldChange(i, guess);
      }
    }
  }, [inferred, dynamicFields, template.variableCount, onDynamicFieldChange]);

  const hasHeaderText =
    template.header?.type === "text" &&
    (template.header.variableCount ?? 0) > 0;

  const hasHeaderMedia =
    template.header?.type === "image" ||
    template.header?.type === "video" ||
    template.header?.type === "document";

  const variableIndices = Array.from(
    { length: template.variableCount },
    (_, i) => i + 1,
  );

  /* ─── Media upload ─── */
  const processFile = useCallback(
    (file: File) => {
      setMediaError(null);
      if (!ALLOWED_TYPES.includes(file.type)) {
        setMediaError("Solo se permiten PNG, JPG o WebP");
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setMediaError("La imagen no puede superar 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => onHeaderMediaChange(e.target?.result as string);
      reader.readAsDataURL(file);
    },
    [onHeaderMediaChange],
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  if (
    template.variableCount === 0 &&
    !hasHeaderText &&
    !hasHeaderMedia
  ) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center gap-2 mb-2">
          <Hash size={18} className="text-emerald-500" />
          <h3 className="font-semibold text-slate-900">
            Personalizar mensaje
          </h3>
          <span className="text-xs text-slate-400 ml-auto">Paso 3</span>
        </div>
        <p className="text-sm text-slate-500 mt-3 leading-relaxed">
          Este template no tiene variables. Podés enviarlo tal cual está.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      <div className="flex items-center gap-2 mb-5">
        <Hash size={18} className="text-emerald-500" />
        <h3 className="font-semibold text-slate-900">Personalizar mensaje</h3>
        <span className="text-xs text-slate-400 ml-auto">Paso 3</span>
      </div>

      <div className="space-y-5">
        {/* Header media (image/video/document) */}
        {hasHeaderMedia && (
          <div>
            <label className="flex items-center gap-1.5 mb-2">
              <ImageIcon size={14} className="text-slate-400" />
              <span className="text-sm font-medium text-slate-700">
                Imagen de encabezado
              </span>
              <span className="text-xs text-slate-400">
                (requerido por el template)
              </span>
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleFileSelect}
              className="hidden"
            />

            {headerMediaUrl ? (
              <div className="relative group rounded-xl overflow-hidden border border-slate-200">
                <img
                  src={headerMediaUrl}
                  alt="Header"
                  className="w-full h-40 object-cover"
                />
                <button
                  onClick={() => onHeaderMediaChange(null)}
                  className="absolute top-2 right-2 px-3 py-1.5 bg-white/90 backdrop-blur-sm text-xs font-medium rounded-lg shadow-sm hover:bg-red-50 hover:text-red-600 transition"
                >
                  Eliminar
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                className={`
                  border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 cursor-pointer group
                  ${
                    isDragging
                      ? "border-emerald-400 bg-emerald-50/40 scale-[1.01]"
                      : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/20"
                  }
                `}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2 transition-colors ${
                    isDragging
                      ? "bg-emerald-100"
                      : "bg-slate-100 group-hover:bg-emerald-100"
                  }`}
                >
                  <ImageIcon
                    size={20}
                    className={`transition-colors ${
                      isDragging
                        ? "text-emerald-500"
                        : "text-slate-400 group-hover:text-emerald-500"
                    }`}
                  />
                </div>
                <p className="text-xs text-slate-500">
                  {isDragging
                    ? "Soltá la imagen aquí"
                    : "Arrastrá la imagen o hacé clic para subir"}
                </p>
                <p className="text-[10px] text-slate-300 mt-1">
                  PNG, JPG, WebP hasta 5MB
                </p>
              </div>
            )}

            {mediaError && (
              <p className="text-xs text-red-500 mt-2 animate-fade-in">
                {mediaError}
              </p>
            )}
          </div>
        )}

        {/* Header text variable */}
        {hasHeaderText && (
          <div>
            <label className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <TypeIcon size={14} className="text-slate-400" />
                Encabezado <span className="text-slate-400">·</span>
                <code className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded text-[11px] font-mono">
                  {"{{1}}"}
                </code>
              </span>
            </label>
            <p className="text-xs text-slate-500 mb-2 italic leading-relaxed">
              Plantilla:{" "}
              <span className="text-slate-700 font-mono">
                {template.header?.text}
              </span>
            </p>
            <input
              type="text"
              value={headerVariable || ""}
              onChange={(e) => onHeaderVariableChange(e.target.value)}
              placeholder="Ej: Juan"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder:text-slate-400
                focus:outline-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-300 transition-all duration-200"
            />
          </div>
        )}

        {/* Body variables */}
        {variableIndices.length > 0 && (
          <div className="space-y-3">
            {/* Auto-fill bar */}
            {variableIndices.some((i) => inferred[i] && !dynamicFields[String(i)]) && (
              <button
                onClick={handleAutoFill}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-gradient-to-r from-violet-50 to-fuchsia-50 hover:from-violet-100 hover:to-fuchsia-100 border border-violet-100 rounded-xl transition-all group"
              >
                <span className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg flex items-center justify-center">
                    <Wand2 size={13} className="text-white" />
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-semibold text-violet-700">
                      Autocompletar con IA
                    </p>
                    <p className="text-[10px] text-slate-500">
                      Detectamos variables que podrían venir del perfil del usuario
                    </p>
                  </div>
                </span>
                <span className="text-xs font-medium text-violet-600 group-hover:translate-x-0.5 transition-transform">
                  Aplicar →
                </span>
              </button>
            )}

            {variableIndices.map((idx) => {
              const dynField = dynamicFields[String(idx)] || null;
              const fieldMeta = DYNAMIC_FIELDS.find((f) => f.key === dynField);
              return (
                <div key={idx}>
                  <label className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                      Variable
                      <code className="text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded text-[11px] font-mono">
                        {"{{" + idx + "}}"}
                      </code>
                      {dynField && (
                        <span className="text-[10px] text-emerald-600 font-medium uppercase tracking-wider ml-1">
                          · Por usuario
                        </span>
                      )}
                    </span>
                    {inferred[idx] && !dynField && (
                      <span className="text-[10px] text-violet-600">
                        Sugerencia: user.{inferred[idx]}
                      </span>
                    )}
                  </label>

                  <PersonalizationToggle
                    variableIndex={idx}
                    dynamicField={dynField}
                    staticValue={bodyVariables[String(idx)] || ""}
                    onChange={({ dynamicField: df, staticValue }) => {
                      onDynamicFieldChange(idx, df);
                      onBodyVariableChange(idx, staticValue);
                    }}
                  />

                  {/* Hint when in static mode */}
                  {!dynField && !bodyVariables[String(idx)] && (
                    <p className="mt-1 text-[10px] text-slate-400 px-1">
                      {VARIABLE_HINTS[idx] || `Valor para variable ${idx}`}
                    </p>
                  )}
                  {/* Preview when in dynamic mode */}
                  {dynField && fieldMeta && (
                    <p className="mt-1 text-[10px] text-slate-500 px-1">
                      Preview: <span className="font-mono text-slate-700">{fieldMeta.example}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default VariableEditor;
